import React, { useEffect, useRef } from 'react';

/**
 * Cloudflare Turnstile widget.
 *
 * Renders the CAPTCHA and calls onVerify(token) when solved. The token must be sent to the backend
 * as `turnstile_token`; the backend verifies it against Cloudflare with the secret key.
 *
 * If no siteKey is provided (e.g. local dev without REACT_APP_TURNSTILE_SITE_KEY), this renders
 * nothing — callers should fall back to the dev bypass, which the backend only honours when
 * NODE_ENV !== 'production'.
 */

const SCRIPT_ID = 'cf-turnstile-script';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

function loadTurnstileScript() {
  return new Promise((resolve, reject) => {
    if (window.turnstile) return resolve();
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', reject);
      return;
    }
    const s = document.createElement('script');
    s.id = SCRIPT_ID;
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

export default function TurnstileWidget({ siteKey, onVerify, onExpire, onError }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onVerifyRef = useRef(onVerify);
  const onExpireRef = useRef(onExpire);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onVerifyRef.current = onVerify;
    onExpireRef.current = onExpire;
    onErrorRef.current = onError;
  }, [onVerify, onExpire, onError]);

  useEffect(() => {
    if (!siteKey) return undefined;
    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current) return;
        if (!window.turnstile) {
          if (onErrorRef.current) onErrorRef.current('script-load-failed');
          return;
        }
        if (widgetIdRef.current !== null) return; // guard against StrictMode double-invoke
        try {
          widgetIdRef.current = window.turnstile.render(containerRef.current, {
            sitekey: siteKey,
            action: 'login',
            callback: (token) => {
              if (onVerifyRef.current) onVerifyRef.current(token);
            },
            'expired-callback': () => {
              if (onExpireRef.current) onExpireRef.current();
            },
            'error-callback': (errorCode) => {
              if (onExpireRef.current) onExpireRef.current();
              if (onErrorRef.current) onErrorRef.current(errorCode);
            },
            'unsupported-callback': () => {
              if (onExpireRef.current) onExpireRef.current();
              if (onErrorRef.current) onErrorRef.current('unsupported');
            },
            'timeout-callback': () => {
              if (onExpireRef.current) onExpireRef.current();
              if (onErrorRef.current) onErrorRef.current('timeout');
            },
            retry: 'never',
            'refresh-expired': 'manual',
            'refresh-timeout': 'manual',
          });
        } catch (renderError) {
          if (onErrorRef.current) onErrorRef.current('widget-render-failed');
        }
      })
      .catch(() => {
        if (onErrorRef.current) onErrorRef.current('script-load-failed');
      });

    return () => {
      cancelled = true;
      try {
        if (widgetIdRef.current !== null && window.turnstile) {
          window.turnstile.remove(widgetIdRef.current);
        }
      } catch (_) { /* noop */ }
      widgetIdRef.current = null;
    };
  }, [siteKey]);

  if (!siteKey) return null;
  return <div ref={containerRef} style={{ marginTop: 8, marginBottom: 8 }} />;
}
